import { countryAtView } from './country-view.ts';
import type { CountryView } from './country-view.ts';
import { TITLE_COUNTRIES } from './title-countries.ts';
import type { Box, Camera } from './viewport.ts';

type Point = readonly [number, number];
type Polygon = readonly (readonly Point[])[];

const countries: ReadonlyMap<string, readonly Polygon[]> = new Map(
  Object.entries(TITLE_COUNTRIES).map(([id, polygons]: readonly [string, readonly (readonly string[])[]]) => [
    id,
    polygons.map((polygon) =>
      polygon.map((ring) =>
        ring.split(';').map((pair): Point => {
          const [lon, lat] = pair.split(',').map(Number);
          if (lon === undefined || lat === undefined) throw new TypeError('invalid title boundary');
          return [lon, lat];
        }),
      ),
    ),
  ]),
);

export interface TitleArea {
  readonly id: string;
  readonly bounds: Box;
  readonly center?: Point;
}

export interface TitleCamera extends Camera {
  readonly center: Point;
}

function inRing(point: Point, ring: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (!a || !b) continue;
    if (a[1] > point[1] !== b[1] > point[1] && point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0])
      inside = !inside;
  }
  return inside;
}

function inPolygon(point: Point, polygon: Polygon): boolean {
  const [outer, ...holes] = polygon;
  return outer !== undefined && inRing(point, outer) && !holes.some((ring) => inRing(point, ring));
}

function awayFromBoundary(point: Point, polygon: Polygon): boolean {
  for (const ring of polygon) {
    for (let i = 1; i < ring.length; i++) {
      const a = ring[i - 1];
      const b = ring[i];
      if (!a || !b) continue;
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const length = dx * dx + dy * dy;
      const t = length === 0 ? 0 : Math.max(0, Math.min(1, ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / length));
      const x = point[0] - a[0] - t * dx;
      const y = point[1] - a[1] - t * dy;
      if (x * x + y * y < 0.004 ** 2) return false;
    }
  }
  return true;
}

const mercator = (lat: number): number =>
  Math.log(Math.tan(Math.PI / 4 + (Math.max(-85, Math.min(85, lat)) * Math.PI) / 360));

function ringArea(ring: readonly Point[], bounds: Box): number {
  let clipped: readonly Point[] = ring.map(([lon, lat]) => [lon, mercator(lat)]);
  const limits = [bounds[0], mercator(bounds[1]), bounds[2], mercator(bounds[3])];
  for (const edge of [0, 1, 2, 3]) {
    const axis = edge % 2;
    const limit = limits[edge];
    if (limit === undefined) continue;
    const next: Point[] = [];
    for (let i = 0; i < clipped.length; i++) {
      const a = clipped[i];
      const b = clipped[(i + 1) % clipped.length];
      if (!a || !b) continue;
      const av = axis === 0 ? a[0] : a[1];
      const bv = axis === 0 ? b[0] : b[1];
      const ain = edge < 2 ? av >= limit : av <= limit;
      const bin = edge < 2 ? bv >= limit : bv <= limit;
      if (ain) next.push(a);
      if (ain !== bin) {
        const t = (limit - av) / (bv - av);
        next.push([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]);
      }
    }
    clipped = next;
  }
  let area = 0;
  for (let i = 0; i < clipped.length; i++) {
    const a = clipped[i];
    const b = clipped[(i + 1) % clipped.length];
    if (a && b) area += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(area) / 2;
}

function boxContains(bounds: Box, center: Point): boolean {
  return center[0] >= bounds[0] && center[0] <= bounds[2] && center[1] >= bounds[1] && center[1] <= bounds[3];
}

export function titleContains(area: TitleArea, center: Point): boolean {
  const polygons = countries.get(area.id);
  return polygons
    ? polygons.some((polygon) => inPolygon(center, polygon) && awayFromBoundary(center, polygon))
    : boxContains(area.bounds, center);
}

export function titleCoverage(area: TitleArea, bounds: Box): number {
  const viewArea = (bounds[2] - bounds[0]) * (mercator(bounds[3]) - mercator(bounds[1]));
  if (viewArea <= 0) return 0;
  const polygons = countries.get(area.id);
  if (polygons) {
    return (
      polygons.reduce((sum, polygon) => {
        const [outer, ...holes] = polygon;
        return sum + (outer ? ringArea(outer, bounds) : 0) - holes.reduce((n, hole) => n + ringArea(hole, bounds), 0);
      }, 0) / viewArea
    );
  }
  const intersection: Box = [
    Math.max(area.bounds[0], bounds[0]),
    Math.max(area.bounds[1], bounds[1]),
    Math.min(area.bounds[2], bounds[2]),
    Math.min(area.bounds[3], bounds[3]),
  ];
  if (intersection[0] >= intersection[2] || intersection[1] >= intersection[3]) return 0;
  return ((intersection[2] - intersection[0]) * (mercator(intersection[3]) - mercator(intersection[1]))) / viewArea;
}

export function selectTitlePlace(
  camera: TitleCamera,
  areas: readonly TitleArea[],
  opening: CountryView | null = null,
): string | null {
  const center: Point = [((((camera.center[0] + 180) % 360) + 360) % 360) - 180, camera.center[1]];
  const pinned = countryAtView(opening, { lon: camera.center[0], lat: camera.center[1], zoom: camera.zoom });
  if (pinned !== null && countries.has(pinned) && areas.some((area) => area.id === pinned)) return pinned;
  const eligible = areas.filter((area) => {
    if (!titleContains(area, center)) return false;
    if (!countries.has(area.id)) {
      const home = [...countries.values()].find((polygons) =>
        polygons.some((polygon) =>
          inPolygon([(area.bounds[0] + area.bounds[2]) / 2, (area.bounds[1] + area.bounds[3]) / 2], polygon),
        ),
      );
      if (home !== undefined && !home.some((polygon) => inPolygon(center, polygon))) return false;
    }
    return titleCoverage(area, camera.bounds) > 0.5;
  });
  if (pinned !== null && eligible.some((area) => area.id === pinned)) return pinned;
  const cities = eligible.filter((area) => !countries.has(area.id));
  const candidates = cities.length > 0 ? cities : eligible;
  if (candidates.length === 0) return null;
  const distance = (area: TitleArea): number => {
    const point: Point = area.center ?? [(area.bounds[0] + area.bounds[2]) / 2, (area.bounds[1] + area.bounds[3]) / 2];
    return ((point[0] - center[0]) * Math.cos((center[1] * Math.PI) / 180)) ** 2 + (point[1] - center[1]) ** 2;
  };
  return (
    candidates.toSorted((a, b) => {
      return (
        distance(a) - distance(b) ||
        (a.bounds[2] - a.bounds[0]) * (mercator(a.bounds[3]) - mercator(a.bounds[1])) -
          (b.bounds[2] - b.bounds[0]) * (mercator(b.bounds[3]) - mercator(b.bounds[1])) ||
        a.id.localeCompare(b.id)
      );
    })[0]?.id ?? null
  );
}
